/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { ErrorTypes } from '../../error/ErrorTypes';

/**
 * ShakaPlayer has a error object reporting mechanism: ShakaPlayer will
 * pass error object with severity, category, code and data which will be
 * used to map the errors and retry-able feasibility
 *
 * To find all the possible errors, refer below page
 * https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html#.Code
 *
 * error object is defined in
 * https://shaka-player-demo.appspot.com/docs/api/lib_util_error.js.html
 *
 * error maping logic has been documented here
 * It describes how to map shaka player errors to Vxxx errors
 * https://confluence.corp.xperi.com/pages/viewpage.action?spaceKey=KST&title=Kaveri+UX+Error+Codes#KaveriUXErrorCodes-NewPlayerErrors
 *
 * */

// using release build
const shaka = require('shaka-player/shaka-player.compiled');

export type ErrorMessageTypeData = {
    message: string;
    type: string;
};

export abstract class ShakaPlayerErrorMapper {
    private static TAG: string = 'ShakaPlayerErrorMapper';

    // const
    private static ERROR_CONVERSION_CONSTANT = 1000;

    public static isErrorHandlingRequired(error: any, videoUrl: string): boolean {
        let isError = true;

        // Load can be interrupted by unload or another load
        if (error?.code === shaka.util.Error.Code.LOAD_INTERRUPTED) {
            isError = false;
        }
        if (error?.data.length > 0) {
            const errorUrl = error.data[0];
            const errorCategory = error.category;
            if (
                errorUrl &&
                errorUrl !== videoUrl &&
                errorCategory === shaka.util.Error.Category.NETWORK
            ) {
                isError = false;
            }
        }
        return isError;
    }

    public static isRetryableError(error: any) {
        const errorCode = error.code;
        const errorCategory = error.category;
        let isRetryable: boolean = false;
        switch (errorCategory) {
            case shaka.util.Error.Category.TEXT:
            case shaka.util.Error.Category.MEDIA:
                isRetryable = true;
                break;
            case shaka.util.Error.Category.NETWORK:
            case shaka.util.Error.Category.MANIFEST:
            case shaka.util.Error.Category.STREAMING:
            case shaka.util.Error.Category.CAST:
            case shaka.util.Error.Category.STORAGE:
            case shaka.util.Error.Category.ADS:
                isRetryable = false;
                break;

            case shaka.util.Error.Category.DRM:
                if (ShakaPlayerErrorMapper.getHTTPErrorCode(error)) isRetryable = false;
                else if (
                    errorCode === shaka.util.Error.Code.LICENSE_REQUEST_FAILED ||
                    errorCode === shaka.util.Error.Code.LICENSE_RESPONSE_REJECTED
                )
                    isRetryable = true;
                break;

            case shaka.util.Error.Category.PLAYER:
                if (
                    errorCode === shaka.util.Error.Code.LOAD_INTERRUPTED ||
                    errorCode === shaka.util.Error.Code.OPERATION_ABORTED ||
                    errorCode === shaka.util.Error.Code.CONTENT_NOT_LOADED
                )
                    isRetryable = true;
                break;
            default:
                isRetryable = true;
                break;
        }
        return isRetryable;
    }

    public static getSubErrorCode(error: any): string {
        const httpCode = ShakaPlayerErrorMapper.getHTTPErrorCode(error);
        if (httpCode) {
            return httpCode;
        }
        const errorCode = error.code;
        const subErrorCode = (errorCode % ShakaPlayerErrorMapper.ERROR_CONVERSION_CONSTANT)
            .toString()
            .padStart(3, '0');
        return subErrorCode;
    }

    public static getHTTPErrorCode(error: any): string {
        if (error.code === shaka.util.Error.Code.LICENSE_REQUEST_FAILED) {
            const httpCode: string =
                error.data[0] && error.data[0].data[1] ? `${error.data[0].data[1]}` : '';
            if (httpCode === '401' || httpCode === '403') return httpCode;
        }
        return '';
    }

    public static mapUiError(error: any): string {
        const errorCategory = error.category;
        let uiErrorCode = '';
        switch (errorCategory) {
            case shaka.util.Error.Category.NETWORK:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_NETWORK_ERROR_V951;
                break;

            case shaka.util.Error.Category.TEXT:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_TEXT_ERROR_V952;
                break;

            case shaka.util.Error.Category.MEDIA:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_MEDIA_ERROR_V953;
                break;

            case shaka.util.Error.Category.MANIFEST:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_MANIFEST_ERROR_V954;
                break;

            case shaka.util.Error.Category.STREAMING:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_STREAMING_MEDIA_V955;
                break;

            case shaka.util.Error.Category.DRM:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_DRM_ERROR_V956;
                if (ShakaPlayerErrorMapper.getHTTPErrorCode(error)) {
                    uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_ENFORCEMENT_ERROR_V948;
                }
                break;

            case shaka.util.Error.Category.PLAYER:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_PLAYER_ERROR_V957;
                break;

            case shaka.util.Error.Category.CAST:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_CAST_ERROR_V958;
                break;

            case shaka.util.Error.Category.STORAGE:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_STORAGE_ERROR_V959;
                break;

            case shaka.util.Error.Category.ADS:
                uiErrorCode = ErrorTypes.LIVE_TV_SHAKA_PLAYER_ADS_ERROR_V960;
                break;

            default:
                break;
        }
        return uiErrorCode;
    }

    /**
     * Returns an object which is of type ErrorMessageTypeData
     * Reference link:- "https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html"
     * @param errorCategory
     * @returns ErrorMessageTypeData {message, type}
     */
    public static mapErrorMessageType(errorCategory: number): ErrorMessageTypeData {
        const errorMessageType: ErrorMessageTypeData = { message: '', type: '' };
        switch (errorCategory) {
            case shaka.util.Error.Category.NETWORK:
                errorMessageType.message = 'Errors from the network stack.';
                errorMessageType.type = 'PLAYER_NETWORK_ERROR';
                break;

            case shaka.util.Error.Category.TEXT:
                errorMessageType.message = 'Errors parsing text streams.';
                errorMessageType.type = 'PLAYER_TEXT_ERROR';
                break;

            case shaka.util.Error.Category.MEDIA:
                errorMessageType.message = 'Errors parsing or processing audio or video streams.';
                errorMessageType.type = 'PLAYER_MEDIA_ERROR';
                break;

            case shaka.util.Error.Category.MANIFEST:
                errorMessageType.message = 'Errors parsing the Manifest.';
                errorMessageType.type = 'PLAYER_MANIFEST_ERROR';
                break;

            case shaka.util.Error.Category.STREAMING:
                errorMessageType.message = 'Errors related to streaming';
                errorMessageType.type = 'PLAYER_STREAMING_ERROR';
                break;

            case shaka.util.Error.Category.DRM:
                errorMessageType.message = 'Errors related to DRM.';
                errorMessageType.type = 'PLAYER_DRM_ERROR';
                break;

            case shaka.util.Error.Category.PLAYER:
                errorMessageType.message = 'Miscellaneous errors from the player.';
                errorMessageType.type = 'PLAYER_MISCELLANEOUS_ERROR';
                break;

            case shaka.util.Error.Category.CAST:
                errorMessageType.message = 'Errors related to cast.';
                errorMessageType.type = 'PLAYER_CAST_ERROR';
                break;

            case shaka.util.Error.Category.STORAGE:
                errorMessageType.message = 'Errors in the database storage (offline).';
                errorMessageType.type = 'PLAYER_STORAGE_ERROR';
                break;

            case shaka.util.Error.Category.ADS:
                errorMessageType.message = 'Errors related to ad insertion';
                errorMessageType.type = 'PLAYER_ADS_ERROR';
                break;

            default:
                break;
        }
        return errorMessageType;
    }
}
